import {
  activateOnboarding,
  verifyOnboarding,
  VerifyResponse,
} from "@/services/onboarding";
import { auth } from "@/lib/firebase";
import { PROFILE_URL } from "@/shared/environmentVariables";
import UserService from "@/services/user";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { signInWithCustomToken } from "firebase/auth";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import AuthWrapper from "./AuthWrapper";

const LOADING_STAGES = [
  { key: "onboarding_stageCreatingUser", delay: 0 },
  { key: "onboarding_stageCreatingCompany", delay: 800 },
  { key: "onboarding_stageAlmostDone", delay: 1500 },
];

function redirectAfterOnboarding(redirectPath?: string | null) {
  const url = new URL(PROFILE_URL);
  if (redirectPath) {
    url.pathname = url.pathname.replace(/\/$/, "") + redirectPath;
  } else {
    url.pathname = url.pathname.replace(/\/$/, "") + "/project";
    url.searchParams.set("sidebarOpen", "false");
  }
  window.location.href = url.href;
}

async function signInAndRedirect(customToken: string, redirectPath?: string | null) {
  const userCredential = await signInWithCustomToken(auth, customToken);
  const idToken = await userCredential.user.getIdToken();
  await UserService.login({ idToken });
  auth.signOut();
  redirectAfterOnboarding(redirectPath);
}

export default function Onboarding() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const inn = searchParams.get("inn") || "";
  const kpp = searchParams.get("kpp") || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<VerifyResponse | null>(null);

  // Activation state
  const [activating, setActivating] = useState(false);
  const [stageIndex, setStageIndex] = useState(0);
  const [done, setDone] = useState(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    if (!inn || !kpp) {
      setError(t("onboarding_invalidLink"));
      setLoading(false);
      return;
    }

    UserService.logout().then(() => verifyOnboarding(inn, kpp))
      .then(async (result) => {
        if (!result.valid) {
          setError(t("onboarding_linkNotFound"));
          setLoading(false);
          return;
        }

        // Return visit — auto-login without showing landing page
        if (result.alreadyOnboarded) {
          try {
            const activateResult = await activateOnboarding(inn, kpp);
            await signInAndRedirect(activateResult.customToken, result.redirectPath);
          } catch {
            setError(t("onboarding_activationError"));
            setLoading(false);
          }
          return;
        }

        setData(result);
        setLoading(false);
      })
      .catch(() => {
        setError(t("onboarding_verificationError"));
        setLoading(false);
      });
  }, [inn, kpp, t]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach(clearTimeout);
    };
  }, []);

  const handleActivate = useCallback(async () => {
    setActivating(true);
    setStageIndex(0);

    // Start pseudo-loading stage timers
    LOADING_STAGES.forEach((stage, index) => {
      if (index === 0) return;
      const timer = setTimeout(() => setStageIndex(index), stage.delay);
      timersRef.current.push(timer);
    });

    try {
      const result = await activateOnboarding(inn, kpp);
      await signInAndRedirect(result.customToken, data?.redirectPath);

      setDone(true);
    } catch {
      setActivating(false);
      setError(t("onboarding_activationError"));
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    }
  }, [inn, kpp, t]);

  // Verify loading or return-visit auto-login in progress
  if (loading) {
    return (
      <AuthWrapper isOnboarding={true}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 300,
          }}
        >
          <CircularProgress />
        </Box>
      </AuthWrapper>
    );
  }

  // Error state
  if (error && !activating) {
    return (
      <AuthWrapper isOnboarding={true}>
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        </Box>
      </AuthWrapper>
    );
  }

  // Activation in progress — pseudo-loading screen
  if (activating) {
    return (
      <AuthWrapper isOnboarding={true}>
        <Box
          sx={{
            width: "100%",
            maxWidth: 400,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
          }}
        >
          {done ? (
            <Typography variant="h5" sx={{ fontWeight: 700, color: "success.main" }}>
              {t("onboarding_stageDone")}
            </Typography>
          ) : (
            <>
              <CircularProgress size={48} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t(LOADING_STAGES[stageIndex].key)}
              </Typography>
            </>
          )}
        </Box>
      </AuthWrapper>
    );
  }

  return (
    <AuthWrapper isOnboarding={true}>
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
        }}
      >
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1.5 }}>
            {t("onboarding_title")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {data?.companyName}
          </Typography>
        </Box>


        <Box sx={{ mt: 4 }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            sx={{ py: 1.5 }}
            onClick={handleActivate}
          >
            {t("onboarding_proceed")}
          </Button>
        </Box>
      </Box>
    </AuthWrapper>
  );
}
